// =============================================================================
// MeuDriver — Batch O2: useLessonPackages
// Hooks para gerenciar pacotes de aulas criados por instrutores.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface InstructorPackage {
  id: string
  instructor_id: string
  name: string
  lesson_count: number
  price: number
  description: string | null
  is_active: boolean
  created_at: string
}

export interface StudentInstructorPackage {
  id: string
  student_id: string
  package_id: string
  lessons_remaining: number
  purchased_at: string
  payment_status: 'pending' | 'paid' | 'refunded' | 'failed'
  package?: InstructorPackage
}

export interface CreatePackagePayload {
  instructor_id: string
  name: string
  lesson_count: number
  price: number
  description?: string | null
  is_active?: boolean
}

export interface UpdatePackagePayload {
  id: string
  name?: string
  lesson_count?: number
  price?: number
  description?: string | null
  is_active?: boolean
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const pkgKeys = {
  all: ['instructor-packages'] as const,
  byInstructor: (instructorId: string) => [...pkgKeys.all, instructorId] as const,
  studentPkgs: (studentId: string) => ['student-instructor-packages', studentId] as const,
}

// ---------------------------------------------------------------------------
// useInstructorPackages — lista pacotes de um instrutor (visão pública e própria)
// ---------------------------------------------------------------------------

export function useInstructorPackages(instructorId: string | undefined) {
  return useQuery<InstructorPackage[]>({
    queryKey: pkgKeys.byInstructor(instructorId ?? ''),
    enabled: Boolean(instructorId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_packages')
        .select('*')
        .eq('instructor_id', instructorId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as InstructorPackage[]
    },
  })
}

// ---------------------------------------------------------------------------
// useCreatePackage — cria um novo pacote para o instrutor autenticado
// ---------------------------------------------------------------------------

export function useCreatePackage() {
  const { user } = useAuth()
  const qc = useQueryClient()

  return useMutation<InstructorPackage, Error, Omit<CreatePackagePayload, 'instructor_id'>>({
    mutationFn: async (payload) => {
      if (!user?.id) throw new Error('Não autenticado')

      // Busca o instructor_id a partir do profile_id
      const { data: instrData, error: instrErr } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user.id)
        .single()

      if (instrErr || !instrData) throw new Error('Instrutor não encontrado')

      const { data, error } = await supabase
        .from('instructor_packages')
        .insert({
          instructor_id: instrData.id,
          name: payload.name,
          lesson_count: payload.lesson_count,
          price: payload.price,
          description: payload.description ?? null,
          is_active: payload.is_active ?? true,
        })
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as InstructorPackage
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: pkgKeys.byInstructor(data.instructor_id) })
      toast.success('Pacote criado com sucesso.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdatePackage — atualiza um pacote existente
// ---------------------------------------------------------------------------

export function useUpdatePackage() {
  const qc = useQueryClient()

  return useMutation<InstructorPackage, Error, UpdatePackagePayload>({
    mutationFn: async ({ id, ...payload }) => {
      const { data, error } = await supabase
        .from('instructor_packages')
        .update(payload)
        .eq('id', id)
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as InstructorPackage
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: pkgKeys.byInstructor(data.instructor_id) })
      toast.success('Pacote atualizado.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useDeletePackage — remove (soft delete via is_active=false) um pacote
// ---------------------------------------------------------------------------

export function useDeletePackage() {
  const qc = useQueryClient()

  return useMutation<void, Error, { id: string; instructorId: string }>({
    mutationFn: async ({ id }) => {
      const { error } = await supabase
        .from('instructor_packages')
        .update({ is_active: false })
        .eq('id', id)

      if (error) throw new Error(error.message)
    },
    onSuccess: (_, { instructorId }) => {
      qc.invalidateQueries({ queryKey: pkgKeys.byInstructor(instructorId) })
      toast.success('Pacote desativado.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useStudentInstructorPackages — pacotes de instrutores comprados pelo aluno
// ---------------------------------------------------------------------------

export function useStudentInstructorPackages() {
  const { user } = useAuth()

  return useQuery<StudentInstructorPackage[]>({
    queryKey: pkgKeys.studentPkgs(user?.id ?? ''),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_instructor_packages')
        .select('*, package:instructor_packages(*)')
        .eq('student_id', user!.id)
        .order('purchased_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as unknown as StudentInstructorPackage[]
    },
  })
}

// ---------------------------------------------------------------------------
// usePurchaseInstructorPackage — compra um pacote de instrutor via RPC
// ---------------------------------------------------------------------------

export function usePurchaseInstructorPackage() {
  const { user } = useAuth()
  const qc = useQueryClient()

  return useMutation<string, Error, { package_id: string }>({
    mutationFn: async ({ package_id }) => {
      const { data, error } = await supabase.rpc('purchase_instructor_lesson_package', {
        p_package_id: package_id,
      })
      if (error) throw new Error(error.message)
      return data as string
    },
    onSuccess: () => {
      if (user?.id) {
        qc.invalidateQueries({ queryKey: pkgKeys.studentPkgs(user.id) })
      }
      toast.success('Pacote adquirido com sucesso!')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}
